import { isEqual, isNumber, range, sortBy } from 'lodash-es';
import { db } from '../../db';
import { type Session } from '../../types';
import { logger } from './logger';

export const deleteFinishedRecordings = async (currentRecordingId: string | null) => {
  const sessions = await db.sessions.toArray();

  // IndexedDB can hand back a null or corrupt row, and reading `.recordings`
  // off it throws and kills the cleanup task. Drop those rows and note it.
  const validSessions = sessions.filter((s): s is Session => s?.recordings != null);
  if (validSessions.length !== sessions.length) {
    logger.warn({
      event_type: 'CLEANUP_RECORDINGS_INVALID_SESSION',
      details: { total: sessions.length, valid: validSessions.length },
    });
  }

  const recordings = validSessions
    .flatMap((s) => s.recordings.map((r) => ({ sessionId: s.id, recording: r })))
    .filter((r) => r.recording.id !== currentRecordingId);

  const finishedRecordings = recordings.filter(
    ({ recording: { backupRecording, chunks, lastChunkIndex, endDate } }) => {
      if (!endDate) return;
      if (!isNumber(lastChunkIndex)) return;
      if (backupRecording && !backupRecording.isProcessed) return;
      const sequence = sortBy(chunks.flatMap((c) => (c.isProcessed ? c.index : [])));
      const done = isEqual(range(0, lastChunkIndex + 1), sequence);

      if (!done && chunks.every((c) => c.isProcessed)) {
        logger.warn({
          event_type: 'CLEANUP_RECORDING_INVALID_SEQUENCE',
          details: { lastChunkIndex, sequence },
        });
      }

      return done;
    }
  );

  if (finishedRecordings.length === 0) return;

  logger.debug({
    event_type: 'DELETE_RECORDINGS_START',
    details: { recordingIds: finishedRecordings.map((r) => r.recording.id) },
  });

  await Promise.all(
    finishedRecordings.map(({ sessionId, recording }) =>
      db.deleteRecording(sessionId, recording.id)
    )
  );

  logger.debug({ event_type: 'DELETE_RECORDINGS_DONE' });
};
